import React, { FC, useState, useMemo } from "react";
import SearchInput from "./SearchInput";
import OptionGrid from "./OptionGrid";
import NoSearchResults from "./NoSearchResults";
import styles from "../../../styles/B2C/StepLayout.module.scss";
import { useWizardOptions } from "../../../context/WizardOptionsContext";

// Show only the first N options (in response order) until the user searches;
// a search query then filters the full list.
const DEFAULT_VISIBLE_COUNT = 20;

interface CityStepProps {
  selected: string;
  onSelect: (city: string) => void;
}

const CityStep: FC<CityStepProps> = ({ selected, onSelect }) => {
  const { cities } = useWizardOptions();
  const cityNames = useMemo(
    () => (cities ?? []).map((city) => city.label),
    [cities]
  );

  const [search, setSearch] = useState("");

  const filteredCities = useMemo(() => {
    if (!search.trim()) return cityNames.slice(0, DEFAULT_VISIBLE_COUNT);
    return cityNames.filter((cityName) =>
      cityName.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, cityNames]);

  const handleSelect = (city: string) => {
    onSelect(city);
  };

  // CFM-1514: dynamic count — round down to nearest 10 + "+" (fallback while loading)
  const cityCountLabel =
    cityNames.length >= 10
      ? `${Math.floor(cityNames.length / 10) * 10}+`
      : "190+";

  return (
    <div className={styles.stepContent}>
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search city..."
        sticky
      />
      {/* CFM-1514: "Top cities" hint shown only in the default (non-search) view */}
      {!search.trim() && (
        <div className={styles.topCities}>
          <h3 className={styles.topCitiesHeading}>Top cities</h3>
          <p className={styles.topCitiesHelper}>
            Don&apos;t see your city?{" "}
            <span className={styles.topCitiesHelperStrong}>Search above</span> — we
            cover {cityCountLabel} cities.
          </p>
        </div>
      )}
      {search.trim() !== "" && filteredCities.length === 0 ? (
        <NoSearchResults />
      ) : (
        <OptionGrid
          options={filteredCities}
          selected={selected ? [selected] : []}
          onSelect={handleSelect}
          multiSelect={false}
        />
      )}
    </div>
  );
};

export default CityStep;
